/**
 * Recording an inspection — `inspections/new/:vehicleId`, `?kind=recheck` for a re-check (PLAN §6).
 *
 * A full page for a phone at the yard, not a sheet: confirm the car, take the six shots, read the
 * odometer, re-grade what is open, grade the five sections, preview the certificate, submit.
 *
 * WHAT THE PAGE READS. The board (one get_inspection_board call) lists the car's contract rows and,
 * in held_without_contract, each held car with no contract in progress today. A board row is a
 * contract that owes today's round OR is in progress, so a contract that ended after the window
 * closed keeps its row for the rest of the month: a row alone is NOT proof of a contract in
 * progress. Each row's `in_progress` is the server's reading of whether its contract is
 * IN_PROGRESS today, by the rule record_inspection files a visit by. The page takes the car's newest
 * row reading true as its contract in progress (`activeRowOf`); with none, a monthly is refused here
 * as the server would refuse it, and a re-check records a visit between contracts. The contract can
 * still end between the read and the submit, so a re-check's success screen reads back the contract
 * record_inspection gave the visit before it says who gets the car back.
 * get_contract_inspections, read through `?contract=` at once (in parallel with the board), else the
 * board's contract, else the car's latest one — an admin always gets the car's view — gives the
 * header, the checklist, the car's open items and its visits. record_inspection derives the
 * contract a visit belongs to itself; the page never sends one.
 *
 * WHAT THE PAGE DECIDES. Nothing about the car. The preview banner applies the visit-result rule to
 * the lines about to be sent and says so; after Submit, the success screen reads the server's
 * `held` and `released`. The checks before Submit mirror the server's refusals so the inspector is
 * not sent to a refusal the page could see coming; the server remains the judge.
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertTriangle, ArrowLeft, CheckCircle2, Loader2, OctagonAlert, RotateCcw, RotateCw, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import {
    KIND_LABEL,
    RESULT_LABEL,
    SLOT_LABEL,
    SLOT_ORDER,
    activeRowOf,
    capturePath,
    contractInspectionPath,
    formatInspectionDay,
    formatKm,
    formatKmChange,
    formatShortDay,
    inspectionRefusal,
    lineLabel,
    offersMonthly,
    REFUSALS_THE_CAR_CHANGED,
    whyNoMonthly,
    type BoardRow,
    type Checklist,
    type HeldWithoutContract,
    type ContractInspections,
    type InspectionKind,
    type InspectionRefusal,
    type OpenItem,
    type PhotoSlot,
    type RecordInspectionResult,
    type Visit,
} from '@/lib/inspections';
import {
    applyAllOk,
    browserStorage,
    buildSubmission,
    carriedPhotoItems,
    createDraft,
    deleteDraft,
    draftItems,
    draftProblems,
    effectiveGrade,
    isResumable,
    linesInForce,
    newClientRef,
    parseKm,
    photoRequired,
    readDraft,
    reconcileDraft,
    setLine,
    writeDraft,
    type InspectionDraft,
} from '@/lib/inspectionDraft';
import { recheckBanner, resultBanner, successHeadline } from '@/lib/inspectionResult';
import { coverExpiryStatus } from '@/lib/coverExpiry';
import {
    fetchContractInspections,
    inspectionKeys,
    recordInspection,
    useCarBasics,
    useContractInspections,
    useInspectionBoard,
    useInspectionChecklist,
    useLatestContractOfCar,
} from '@/components/inspections/inspectionApi';
import { GradeBadge, Pill } from '@/components/inspections/InspectionChip';
import { PhotoThumb } from '@/components/inspections/PhotoZoom';
import PhotoSlots from '@/components/inspections/capture/PhotoSlots';
import CheckRow, { type RowProblem } from '@/components/inspections/capture/CheckRow';
import { scrollToLine } from '@/components/inspections/capture/scrollToLine';
import ChecklistSection from '@/components/inspections/capture/ChecklistSection';
import RecheckList from '@/components/inspections/capture/RecheckList';
import { usePhotoUploads, type PhotoUpload } from '@/components/inspections/capture/usePhotoUploads';

/** The layout's sticky header height, so this page's own sticky bar sits just under it. */
function useLayoutHeaderHeight(): number {
    const [height, setHeight] = useState(0);
    useLayoutEffect(() => {
        const el = document.querySelector('body header');
        if (!el) return;
        const measure = () => setHeight(el.getBoundingClientRect().height);
        measure();
        if (typeof ResizeObserver === 'undefined') return;
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);
    return height;
}

interface Picture {
    vehicleId: number;
    data: ContractInspections | null;
    activeRow: BoardRow | null;
    heldCar: HeldWithoutContract | null;
    checklist: Checklist;
    today: string;
    keyContract: number | null;
    plate: string | null;
    makeModel: string;
}

const NO_OPEN_ITEMS: OpenItem[] = [];

function Section({ step, title, children, aside }: { step: number; title: string; children: React.ReactNode; aside?: React.ReactNode }) {
    return (
        <section className="space-y-3">
            <div className="flex items-baseline justify-between gap-3">
                <h2 className="text-lg font-semibold tracking-tight">
                    <span className="mr-2 text-muted-foreground tabular-nums">{step}</span>
                    {title}
                </h2>
                {aside}
            </div>
            {children}
        </section>
    );
}

// ─── The page: resolve the car, then hand a settled picture to the form ─────

export default function InspectionCapture() {
    const { vehicleId: vehicleParam } = useParams();
    const vehicleId = Number(vehicleParam) || 0;
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const kind: InspectionKind = params.get('kind') === 'recheck' ? 'recheck' : 'monthly';
    const contractParam = Number(params.get('contract')) || null;
    const headerHeight = useLayoutHeaderHeight();
    const queryClient = useQueryClient();
    const [generation, setGeneration] = useState(0);
    const [readingFresh, setReadingFresh] = useState(false);

    const [frozen, setFrozen] = useState<Picture | null>(null);

    const board = useInspectionBoard(null, vehicleId > 0);
    const liveRow = useMemo(() => activeRowOf(board.data, vehicleId), [board.data, vehicleId]);
    const liveHeldCar = board.data?.held_without_contract.find((c) => c.vehicle_id === vehicleId) ?? null;
    const needLatest = board.isSuccess && !liveRow && !contractParam;
    const latest = useLatestContractOfCar(vehicleId, needLatest);
    const resolved = board.isSuccess && (!needLatest || latest.isSuccess);
    // `?contract=` is read at once, beside the board; the key moves only if the board names another.
    const liveKey = resolved ? (liveRow?.contract_id ?? contractParam ?? (needLatest ? latest.data ?? null : null)) : contractParam;
    const payload = useContractInspections(liveKey);
    const noContract = resolved && liveKey === null;
    const checklistQ = useInspectionChecklist(noContract);
    const basics = useCarBasics(vehicleId, noContract && !liveHeldCar);

    const liveData: ContractInspections | null = payload.data ?? null;
    const wrongCar = !!liveData && liveData.header.vehicle_id !== vehicleId;
    const liveChecklist: Checklist | undefined = liveData?.checklist ?? checklistQ.data;
    const liveToday = liveData?.current.today ?? board.data?.today ?? null;
    const error = board.error ?? latest.error ?? payload.error ?? checklistQ.error ?? null;
    const loading =
        !error &&
        (!resolved ||
            (liveKey !== null && payload.isLoading) ||
            (noContract && checklistQ.isLoading) ||
            (noContract && !liveHeldCar && basics.isLoading));
    const missing = !loading && !error && (wrongCar || (liveKey !== null && !liveData) || (noContract && !liveChecklist));

    /*
     * THE PICTURE THE FORM WORKS FROM IS TAKEN ONCE. The board and the car's view refetch in the
     * background, and the submit itself invalidates them; a refetch must neither move the lines while
     * the inspector grades them nor swap the success screen for "this round's monthly is recorded".
     * "Start again" and "Reload the car" read the car again first (the queries do not refetch on
     * focus), then take a fresh picture; Start again also starts a new form.
     */
    const carBasics = basics.data ?? null;
    const live = useMemo<Picture | null>(
        () =>
            !loading && !error && !missing && liveToday && liveChecklist
                ? {
                      vehicleId,
                      data: liveData,
                      activeRow: liveRow,
                      heldCar: liveHeldCar,
                      checklist: liveChecklist,
                      today: liveToday,
                      keyContract: liveKey,
                      plate: liveData?.header.plate ?? liveHeldCar?.plate ?? carBasics?.plate ?? null,
                      makeModel: [liveData?.header.make ?? liveHeldCar?.make ?? carBasics?.make, liveData?.header.model ?? liveHeldCar?.model ?? carBasics?.model]
                          .filter(Boolean)
                          .join(' '),
                  }
                : null,
        [loading, error, missing, liveToday, liveChecklist, vehicleId, liveData, liveRow, liveHeldCar, liveKey, carBasics],
    );
    useEffect(() => {
        if (live && (!frozen || frozen.vehicleId !== vehicleId)) setFrozen(live);
    }, [live, frozen, vehicleId]);
    const view = frozen && frozen.vehicleId === vehicleId ? frozen : live;

    const readAgain = useCallback(
        async (fresh: boolean) => {
            if (fresh) setReadingFresh(true);
            try {
                await queryClient.invalidateQueries({ queryKey: inspectionKeys.all });
            } finally {
                setFrozen(null);
                if (fresh) {
                    setGeneration((g) => g + 1);
                    setReadingFresh(false);
                }
            }
        },
        [queryClient],
    );

    const data = view?.data ?? null;
    const activeRow = view?.activeRow ?? null;
    const heldCar = view?.heldCar ?? null;
    const checklist = view?.checklist;
    const today = view?.today ?? null;
    const keyContract = view?.keyContract ?? null;
    const plate = view?.plate ?? null;
    const makeModel = view?.makeModel ?? '';
    const visits: Visit[] = data?.visits ?? [];
    const openItems: OpenItem[] = data?.open_items ?? NO_OPEN_ITEMS;
    const heldBefore = data ? data.current.condition === 'held' : (heldCar?.open_fix_now ?? 0) > 0;
    const backTo = keyContract ? contractInspectionPath(keyContract) : '/dashboard/inspections';

    // A monthly is refused by the server on these; say so before the inspector does the work.
    let blocker: { title: string; body: string; link?: { to: string; label: string } } | null = null;
    if (view && kind === 'monthly') {
        if (!activeRow) {
            blocker = whyNoMonthly(null, heldBefore, false);
        } else if (data && !data.current.period) {
            blocker = {
                title: 'No inspection round today',
                body: 'Today is in no inspection round, so no monthly inspection can be recorded yet. A re-check can still be recorded.',
            };
        } else if (data?.current.inspection_id) {
            blocker = {
                title: 'This round’s monthly is recorded',
                body: `The monthly inspection for this round was recorded on ${formatInspectionDay(data.current.inspected_on)}.`,
                link: { to: contractInspectionPath(activeRow.contract_id, data.current.inspection_id), label: 'Open it' },
            };
        } else {
            // The offer rule the board and the contract page apply (PLAN §6 step 2).
            blocker = whyNoMonthly(activeRow.attendance, heldBefore, activeRow.in_progress);
        }
    }

    const frontPhoto = visits.flatMap((v) => v.photos.filter((p) => p.slot === 'front_34'))[0] ?? null;
    const lastReading = visits.find((v) => v.odometer_km !== null) ?? null;
    const roadworthy = data?.header.roadworthy_expires_on ?? null;
    // Good through its expiry date, on the Ghana day (G6) — the rule this page already kept, now the shared one.
    const roadworthyPast = !!today && coverExpiryStatus(roadworthy, today) === 'expired';
    const insurancePast = !!today && coverExpiryStatus(data?.header.insurance_expires_on, today) === 'expired';

    return (
        <div className="mx-auto max-w-3xl animate-fade-in pb-4">
            {/* Confirm the car: sticky, so the plate stays in view through the whole inspection. */}
            <div className="sticky z-30 -mx-4 border-b bg-background/95 px-4 py-2 backdrop-blur sm:mx-0 sm:rounded-b-xl sm:border-x" style={{ top: headerHeight }}>
                <div className="flex items-center gap-3">
                    <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0" onClick={() => navigate(backTo)} aria-label="Back">
                        <ArrowLeft />
                    </Button>
                    <div className="min-w-0 flex-1">
                        <p className="truncate text-lg font-semibold leading-tight">{plate ?? (view ? 'Unregistered' : '…')}</p>
                        <p className="truncate text-sm text-muted-foreground">
                            {makeModel || ' '}
                            {activeRow?.driver_name && ` · ${activeRow.driver_name}`}
                        </p>
                    </div>
                    <Pill tone={kind === 'monthly' ? 'blue' : 'slate'}>{KIND_LABEL[kind]}</Pill>
                </div>
            </div>

            {!view && error ? (
                <Alert variant="destructive" className="mt-4">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>This car could not be loaded: {(error as Error).message}</AlertDescription>
                </Alert>
            ) : !view && !missing ? (
                <div className="mt-4 space-y-3">
                    <Skeleton className="h-28 w-full" />
                    <Skeleton className="h-64 w-full" />
                </div>
            ) : !view || !today ? (
                <Alert variant="destructive" className="mt-4">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                        {wrongCar ? 'That contract is not on this car.' : 'This car’s inspection details could not be found.'}{' '}
                        <Link to="/dashboard/inspections" className="underline">
                            Back to the board
                        </Link>
                    </AlertDescription>
                </Alert>
            ) : (
                <div className="mt-4 space-y-6">
                    {/* The rest of the confirm step. */}
                    <div className="flex gap-3 rounded-xl border bg-card p-3 sm:p-4">
                        {frontPhoto ? (
                            <PhotoThumb url={frontPhoto.url} alt="Last front photo" caption="Last front photo" className="h-20 w-24 sm:h-24 sm:w-32" />
                        ) : (
                            <div className="flex h-20 w-24 shrink-0 items-center justify-center rounded-md border border-dashed text-center text-xs text-muted-foreground sm:h-24 sm:w-32">
                                No earlier photo
                            </div>
                        )}
                        <dl className="min-w-0 space-y-1 text-sm">
                            <div className="flex flex-wrap gap-x-1.5">
                                <dt className="text-muted-foreground">Driver</dt>
                                <dd className="font-medium">{activeRow ? activeRow.driver_name ?? 'No name on file' : 'No contract in progress'}</dd>
                            </div>
                            {data && (
                                <div className="flex flex-wrap gap-x-1.5">
                                    <dt className="text-muted-foreground">Roadworthy</dt>
                                    <dd className={cn(roadworthyPast && 'font-medium text-red-700 dark:text-red-400')}>
                                        {roadworthy ? `${roadworthyPast ? 'expired' : 'until'} ${formatInspectionDay(roadworthy)}` : 'No roadworthy on file'}
                                    </dd>
                                </div>
                            )}
                            {data && activeRow && (
                                <div className="flex flex-wrap gap-x-1.5">
                                    <dt className="text-muted-foreground">Insurance</dt>
                                    <dd className={cn(insurancePast && 'font-medium text-red-700 dark:text-red-400')}>
                                        {data.header.insurance_expires_on
                                            ? `${insurancePast ? 'expired' : 'until'} ${formatInspectionDay(data.header.insurance_expires_on)}`
                                            : 'None on this contract'}
                                    </dd>
                                </div>
                            )}
                            <div className="flex flex-wrap gap-x-1.5">
                                <dt className="text-muted-foreground">Last</dt>
                                <dd>{lastReading ? `${formatShortDay(lastReading.inspected_on)} · ${formatKm(lastReading.odometer_km)}` : 'No reading yet'}</dd>
                            </div>
                        </dl>
                    </div>

                    {heldBefore && (
                        <Alert className="border-red-300 bg-red-50 dark:border-red-900 dark:bg-red-950/30">
                            <OctagonAlert className="h-4 w-4 !text-red-600" />
                            <AlertTitle>This car is held</AlertTitle>
                            <AlertDescription className="space-y-2">
                                <p>
                                    {openItems.filter((o) => o.grade === 'fix_now').length || heldCar?.open_fix_now || 'Some'} must be fixed before release.
                                    {kind === 'monthly' && !blocker ? ' The monthly can release it in the same visit.' : ''}
                                </p>
                            </AlertDescription>
                        </Alert>
                    )}

                    {readingFresh ? (
                        <div className="space-y-3">
                            <Skeleton className="h-28 w-full" />
                            <Skeleton className="h-64 w-full" />
                        </div>
                    ) : blocker ? (
                        <div className="space-y-3 rounded-xl border p-4">
                            <p className="font-semibold">{blocker.title}</p>
                            <p className="text-sm text-muted-foreground">{blocker.body}</p>
                            <div className="flex flex-wrap gap-2">
                                <Button onClick={() => navigate(capturePath(vehicleId, 'recheck', keyContract), { replace: true })}>
                                    <RotateCcw aria-hidden />
                                    Record a re-check
                                </Button>
                                {blocker.link && (
                                    <Button variant="outline" asChild>
                                        <Link to={blocker.link.to}>{blocker.link.label}</Link>
                                    </Button>
                                )}
                            </div>
                        </div>
                    ) : (
                        <CaptureForm
                            key={`${vehicleId}:${kind}:${generation}`}
                            vehicleId={vehicleId}
                            kind={kind}
                            today={today}
                            checklist={checklist!}
                            openItems={openItems}
                            visits={visits}
                            activeContractId={activeRow?.contract_id ?? null}
                            contractKey={keyContract}
                            hasDriver={!!activeRow}
                            heldBefore={heldBefore}
                            lastReading={lastReading}
                            otherKind={
                                kind === 'recheck' && activeRow && offersMonthly(activeRow.attendance, heldBefore, activeRow.in_progress)
                                    ? { label: 'Do the monthly inspection instead', to: capturePath(vehicleId, 'monthly', activeRow.contract_id) }
                                    : kind === 'monthly'
                                      ? { label: 'Record a re-check instead', to: capturePath(vehicleId, 'recheck', keyContract) }
                                      : null
                            }
                            onReadAgain={readAgain}
                        />
                    )}
                </div>
            )}
        </div>
    );
}

// ─── The form ───────────────────────────────────────────────────────────────

interface CaptureFormProps {
    vehicleId: number;
    kind: InspectionKind;
    today: string;
    checklist: Checklist;
    openItems: OpenItem[];
    visits: Visit[];
    /** The contract taken as in progress on the car (see the page header); null between contracts. */
    activeContractId: number | null;
    /** The contract the car's view was read through, for links back. */
    contractKey: number | null;
    hasDriver: boolean;
    heldBefore: boolean;
    lastReading: Visit | null;
    otherKind: { label: string; to: string } | null;
    /** Reads the car again and takes a fresh picture; `fresh` also starts a new form. */
    onReadAgain: (fresh: boolean) => Promise<void>;
}

function CaptureForm(props: CaptureFormProps) {
    const { vehicleId, kind, today, checklist, openItems, visits, activeContractId, contractKey, hasDriver, heldBefore, lastReading } = props;
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const storage = useMemo(() => browserStorage(), []);

    const [stored] = useState(() => readDraft(storage, vehicleId, kind));
    const resumed = !!stored && isResumable(stored, today);
    const [stale, setStale] = useState<InspectionDraft | null>(stored && !resumed ? stored : null);
    const [draft, setDraft] = useState<InspectionDraft>(() =>
        resumed ? reconcileDraft(stored!, openItems) : createDraft({ vehicleId, kind, today, clientRef: newClientRef(), openItems }),
    );
    const [dirty, setDirty] = useState(resumed);
    const [saveFailed, setSaveFailed] = useState(false);
    const [done, setDone] = useState<{ result: RecordInspectionResult; fixNowSent: number; onContract: boolean } | null>(null);
    const [refusal, setRefusal] = useState<InspectionRefusal | null>(null);
    const [showProblems, setShowProblems] = useState(false);
    const [confirmRestart, setConfirmRestart] = useState(false);
    const [reloading, setReloading] = useState(false);
    const photoSeq = useRef(draft.nextPhoto);

    // Every change is kept, once the inspector has changed something; nothing after the server confirms.
    useEffect(() => {
        if (!dirty || done) return;
        setSaveFailed(!writeDraft(storage, draft));
    }, [draft, dirty, done, storage]);

    // A resumed draft, and the draft after "Reload the car", keep only the grades their rows offer.
    useEffect(() => {
        setDraft((d) => reconcileDraft(d, openItems));
    }, [openItems]);

    const update = useCallback((fn: (d: InspectionDraft) => InspectionDraft) => {
        setDirty(true);
        setDraft(fn);
    }, []);

    const uploads = usePhotoUploads({
        vehicleId,
        clientRef: draft.clientRef,
        onUploaded: (target, url) =>
            update((d) =>
                'slot' in target
                    ? { ...d, slots: { ...d.slots, [target.slot]: url } }
                    : setLine(d, target.item, { photos: [...(d.lines[target.item]?.photos ?? []).filter((u) => u !== url), url] }),
            ),
    });

    const inForce = useMemo(() => linesInForce(checklist, today), [checklist, today]);
    const pinned = useMemo(() => new Set(openItems.map((o) => o.item)), [openItems]);
    const carried = useMemo(() => carriedPhotoItems(openItems, visits, activeContractId), [openItems, visits, activeContractId]);
    const items = draftItems(kind, inForce, openItems, draft.extraItems);
    const problems = draftProblems(draft, items, carried);
    const problemsByItem = useMemo(() => {
        const map = new Map<string, Set<RowProblem>>();
        for (const p of problems) {
            if (!p.item) continue;
            if (!map.has(p.item)) map.set(p.item, new Set());
            map.get(p.item)!.add(p.problem as RowProblem);
        }
        return map;
    }, [problems]);

    const previews = useMemo(() => {
        const out: Record<string, string> = {};
        for (const u of Object.values(uploads.uploads)) if (u.url && u.previewUrl) out[u.url] = u.previewUrl;
        return out;
    }, [uploads.uploads]);
    const pendingFor = (item: string): PhotoUpload[] =>
        Object.values(uploads.uploads).filter((u) => 'item' in u.target && u.target.item === item && u.status !== 'done');

    const odometer = parseKm(draft.odometerKm);
    const odometerInvalid = draft.odometerKm.trim() !== '' && odometer === null;
    const lastService = parseKm(draft.lastServiceKm);
    const lastServiceInvalid = draft.lastServiceKm.trim() !== '' && lastService === null;
    const delta = odometer !== null && lastReading?.odometer_km != null ? odometer - lastReading.odometer_km : null;
    const knownService = lastService ?? visits.find((v) => v.last_service_km !== null)?.last_service_km ?? null;
    const sinceService = odometer !== null && knownService !== null ? odometer - knownService : null;

    const grades = items.map((i) => effectiveGrade(draft.lines[i])).filter((g): g is NonNullable<typeof g> => !!g);
    const banner = kind === 'recheck' ? recheckBanner(heldBefore, grades) : resultBanner(grades);
    const bannerTone = banner.startsWith('Held') ? 'red' : banner.startsWith('Passed –') ? 'amber' : 'green';
    const blocked = problems.length > 0 || odometerInvalid || lastServiceInvalid || uploads.busy || reloading;

    const mutation = useMutation({
        mutationFn: async () => {
            const submission = buildSubmission(draft, items);
            const result = await recordInspection({
                vehicleId,
                kind,
                clientRef: draft.clientRef,
                odometerKm: odometer,
                lastServiceKm: lastService,
                remarks: draft.remarks.trim() || null,
                lines: submission.lines,
                photos: submission.photos,
            });
            /*
             * WHO GETS THE CAR BACK. record_inspection files the visit on the contract in progress today, or
             * on none. A monthly always has one; for a re-check the page's reading is the board's
             * `in_progress` when the page read it, and the contract may have ended since, so before saying
             * "Release the car to the driver" the visit is read back and its contract taken from the server.
             */
            let onContract = kind === 'monthly' || hasDriver;
            if (kind === 'recheck' && hasDriver && !result.held && contractKey) {
                try {
                    const recorded = (await fetchContractInspections(contractKey))?.visits.find((v) => v.id === result.inspection_id);
                    if (recorded) onContract = !recorded.between_contracts;
                } catch {
                    // The visit is recorded; the page's own reading stands.
                }
            }
            return { result, fixNowSent: submission.lines.filter((l) => l.grade === 'fix_now').length, onContract };
        },
        onSuccess: (outcome) => {
            // `duplicate: true` counts as confirmed: the first submit landed.
            deleteDraft(storage, vehicleId, kind);
            setDone(outcome);
            setRefusal(null);
            void queryClient.invalidateQueries({ queryKey: inspectionKeys.all });
            // The Records view and the vehicle page read the tables under this key.
            void queryClient.invalidateQueries({ queryKey: ['vehicle-inspections'] });
            window.scrollTo({ top: 0 });
        },
        onError: (error) => {
            const r = inspectionRefusal(error as { message?: string; details?: string });
            if (r.code === 'INSPECTION_ALREADY_RECORDED' && (activeContractId ?? contractKey)) {
                // The draft is kept until it is discarded.
                toast.info(r.sentence);
                navigate(contractInspectionPath((activeContractId ?? contractKey)!, Number(r.detail) || null));
                return;
            }
            setRefusal(r);
            setShowProblems(true);
        },
    });

    const takeItemPhoto = (item: string, file: File) => {
        const n = photoSeq.current;
        photoSeq.current += 1;
        update((d) => ({ ...d, nextPhoto: Math.max(d.nextPhoto, n + 1) }));
        void uploads.start(`${item}-${n}`, { item }, file);
    };

    const renderRow = (item: string, open: OpenItem | null, removable = false) => {
        const line = draft.lines[item] ?? { grade: null, note: '', fixedOnSpot: false, photos: [] };
        const def = checklist.lines.find((l) => l.key === item);
        return (
            <CheckRow
                item={item}
                label={def?.label ?? item}
                guidance={def?.guidance}
                hint={
                    item === 'servicing' && sinceService !== null
                        ? `${formatKm(sinceService)} since the last service (at ${formatKm(knownService)})`
                        : null
                }
                open={open}
                line={line}
                photoRequired={photoRequired(line, item, carried)}
                problems={problemsByItem.get(item) ?? new Set()}
                showProblems={showProblems}
                pending={pendingFor(item)}
                previews={previews}
                onChange={(patch) => update((d) => setLine(d, item, patch))}
                onPhoto={(file) => takeItemPhoto(item, file)}
                onRemovePhoto={(url) => {
                    const entry = Object.values(uploads.uploads).find((u) => u.url === url);
                    if (entry) uploads.dismiss(entry.name);
                    update((d) => setLine(d, item, { photos: (d.lines[item]?.photos ?? []).filter((u) => u !== url) }));
                }}
                onRetry={uploads.retry}
                onDismiss={uploads.dismiss}
                onRemoveLine={
                    removable
                        ? () =>
                              update((d) => {
                                  const lines = { ...d.lines };
                                  delete lines[item];
                                  return { ...d, extraItems: d.extraItems.filter((i) => i !== item), lines };
                              })
                        : undefined
                }
            />
        );
    };

    const restart = () => {
        deleteDraft(storage, vehicleId, kind);
        void props.onReadAgain(true);
    };

    // After a refusal that means the car changed: read it again and keep the answers that still fit.
    const reloadCar = async () => {
        setReloading(true);
        setRefusal(null);
        try {
            await props.onReadAgain(false);
        } finally {
            setReloading(false);
        }
    };

    const firstProblem = () => {
        setShowProblems(true);
        const p = problems.find((x) => x.item);
        const target = p?.item ? document.getElementById(`line-${p.item}`) : odometerInvalid ? document.getElementById('odometer') : null;
        target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    // ── An older draft: it cannot be resumed, only started again. ──
    if (stale) {
        return (
            <div className="space-y-3 rounded-xl border p-4">
                <p className="font-semibold">A draft from {formatInspectionDay(stale.startedOn)} is saved for this car</p>
                <p className="text-sm text-muted-foreground">A draft can be resumed only on the day it was started. Start again to inspect the car today.</p>
                <Button
                    onClick={() => {
                        setStale(null);
                        restart();
                    }}
                >
                    <RotateCcw aria-hidden />
                    Start again
                </Button>
            </div>
        );
    }

    // ── Submitted. ──
    if (done) {
        const { result, fixNowSent, onContract } = done;
        return (
            <div className="space-y-4">
                <div
                    className={cn(
                        'rounded-2xl border-2 p-6 text-center',
                        result.held ? 'border-red-500 bg-red-50 dark:bg-red-950/30' : 'border-emerald-500 bg-emerald-50 dark:bg-emerald-950/30',
                    )}
                >
                    {result.held ? (
                        <OctagonAlert className="mx-auto h-10 w-10 text-red-600" aria-hidden />
                    ) : (
                        <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-600" aria-hidden />
                    )}
                    <h2 className="mt-3 text-2xl font-semibold tracking-tight">{successHeadline(result.held, fixNowSent, onContract)}</h2>
                    <p className="mt-1 text-muted-foreground">
                        {KIND_LABEL[kind]} recorded · {RESULT_LABEL[result.result]}
                        {result.released && ' · released'}
                    </p>
                    {result.duplicate && <p className="mt-2 text-sm text-muted-foreground">This inspection had already reached the server; it was not recorded twice.</p>}
                    {result.warnings.some((w) => w.code === 'INSPECTION_ODOMETER_LOWER') && (
                        <p className="mt-2 text-sm text-orange-700 dark:text-orange-400">
                            The odometer reading is lower than the car’s last one ({formatKm(result.warnings.find((w) => w.code === 'INSPECTION_ODOMETER_LOWER')?.previous_odometer_km)}).
                        </p>
                    )}
                </div>
                <div className="flex flex-col gap-2 sm:flex-row">
                    <Button className="h-11 flex-1" onClick={() => navigate('/dashboard/inspections')}>
                        Back to the board
                    </Button>
                    {contractKey && (
                        <Button variant="outline" className="h-11 flex-1" onClick={() => navigate(contractInspectionPath(contractKey, result.inspection_id))}>
                            Open the certificate
                        </Button>
                    )}
                </div>
            </div>
        );
    }

    const sectionLines = (key: string) => inForce.filter((l) => l.section === key);
    let step = 0;
    const next = () => (step += 1);

    return (
        <div className="space-y-8">
            {(resumed || saveFailed || props.otherKind) && (
                <div className="space-y-2">
                    {resumed && (
                        <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border bg-muted/40 px-3 py-2 text-sm">
                            <span>Resumed today’s draft.</span>
                            <Button variant="ghost" size="sm" onClick={() => setConfirmRestart(true)}>
                                Start again
                            </Button>
                        </div>
                    )}
                    {saveFailed && (
                        <p className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-300">
                            This browser is not keeping a draft, so a reload loses these answers. Keep the page open until you submit.
                        </p>
                    )}
                    {props.otherKind && (
                        <Link to={props.otherKind.to} replace className="inline-block text-sm text-muted-foreground underline underline-offset-4">
                            {props.otherKind.label}
                        </Link>
                    )}
                </div>
            )}

            {kind === 'monthly' && (
                <Section step={next()} title="Photos" aside={<span className="text-xs text-muted-foreground">optional</span>}>
                    <PhotoSlots
                        stored={draft.slots}
                        uploads={uploads.uploads}
                        onPick={(slot: PhotoSlot, file) => void uploads.start(slot, { slot }, file)}
                        onRetry={(slot: PhotoSlot) => uploads.retry(slot)}
                    />
                </Section>
            )}

            <Section step={next()} title="Odometer">
                <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-1.5">
                        <label htmlFor="odometer" className="text-sm font-medium">
                            Reading <span className="font-normal text-muted-foreground">km</span>
                        </label>
                        <Input
                            id="odometer"
                            inputMode="numeric"
                            autoComplete="off"
                            placeholder={lastReading?.odometer_km != null ? String(lastReading.odometer_km) : 'e.g. 48210'}
                            value={draft.odometerKm}
                            onChange={(e) => update((d) => ({ ...d, odometerKm: e.target.value }))}
                            className={cn('h-12 text-lg tabular-nums', odometerInvalid && 'border-red-500')}
                        />
                        <p className="text-xs text-muted-foreground">
                            {lastReading?.odometer_km != null
                                ? `Last ${formatKm(lastReading.odometer_km)} on ${formatShortDay(lastReading.inspected_on)}`
                                : 'No earlier reading on this car.'}
                            {delta !== null && <span className={cn('ml-1.5 font-medium', delta < 0 ? 'text-orange-700 dark:text-orange-400' : 'text-foreground')}>{formatKmChange(delta)}</span>}
                        </p>
                        {odometerInvalid && <p className="text-xs text-red-700 dark:text-red-400">Digits only.</p>}
                        {delta !== null && delta < 0 && (
                            <p className="text-xs text-orange-700 dark:text-orange-400">Lower than the last reading. Check the dashboard; you can still submit.</p>
                        )}
                    </div>
                    <div className="space-y-1.5">
                        <label htmlFor="last-service" className="text-sm font-medium">
                            Last service at <span className="font-normal text-muted-foreground">km, optional</span>
                        </label>
                        <Input
                            id="last-service"
                            inputMode="numeric"
                            autoComplete="off"
                            placeholder="From the oil-change sticker"
                            value={draft.lastServiceKm}
                            onChange={(e) => update((d) => ({ ...d, lastServiceKm: e.target.value }))}
                            className={cn('h-12 text-lg tabular-nums', lastServiceInvalid && 'border-red-500')}
                        />
                        {lastServiceInvalid && <p className="text-xs text-red-700 dark:text-red-400">Digits only.</p>}
                    </div>
                </div>
            </Section>

            {kind === 'monthly' ? (
                <>
                    {openItems.length > 0 && (
                        <Section step={next()} title="Open items">
                            <p className="-mt-1 text-sm text-muted-foreground">Left open by an earlier visit. Say how each one stands today.</p>
                            <div className="space-y-2">
                                {openItems.map((o) => (
                                    <div key={o.item}>{renderRow(o.item, o)}</div>
                                ))}
                            </div>
                        </Section>
                    )}
                    <Section step={next()} title="Checklist">
                        <div className="space-y-8">
                            {[...checklist.sections]
                                .sort((a, b) => a.position - b.position)
                                .map((section) => (
                                    <ChecklistSection
                                        key={section.key}
                                        section={section}
                                        lines={sectionLines(section.key)}
                                        pinned={pinned}
                                        draft={draft}
                                        onAllOk={() => update((d) => applyAllOk(d, sectionLines(section.key).map((l) => l.key), pinned))}
                                        renderRow={(line) => renderRow(line.key, null)}
                                    />
                                ))}
                        </div>
                    </Section>
                </>
            ) : (
                <Section step={next()} title="Re-check">
                    <RecheckList
                        openItems={openItems}
                        extraItems={draft.extraItems}
                        lines={inForce}
                        renderRow={(item, open) => renderRow(item, open, !open)}
                        onAdd={(item) => update((d) => (d.extraItems.includes(item) ? d : { ...d, extraItems: [...d.extraItems, item] }))}
                    />
                </Section>
            )}

            <Section step={next()} title="Remarks" aside={<span className="text-xs text-muted-foreground">optional</span>}>
                <Textarea
                    rows={3}
                    value={draft.remarks}
                    onChange={(e) => update((d) => ({ ...d, remarks: e.target.value }))}
                    placeholder="Anything else about the car"
                />
            </Section>

            {/* Certificate preview (PLAN §6 step 8). */}
            <Section step={next()} title="Check and submit">
                <div className="space-y-4 rounded-xl border p-4">
                    <div
                        className={cn(
                            'rounded-lg px-4 py-3 text-lg font-semibold',
                            bannerTone === 'red' && 'bg-red-600 text-white',
                            bannerTone === 'amber' && 'bg-amber-500 text-white',
                            bannerTone === 'green' && 'bg-emerald-600 text-white',
                        )}
                    >
                        {banner}
                        <span className="block text-xs font-normal opacity-90">Preview of the lines below; the server records the result.</span>
                    </div>

                    {problems.length > 0 && (
                        <div className="space-y-1">
                            <p className="text-sm font-medium text-red-700 dark:text-red-400">Before submitting</p>
                            <ul className="space-y-1 text-sm">
                                {problems.map((p, i) =>
                                    p.item ? (
                                        <li key={`${p.item}-${p.problem}`}>
                                            <a
                                                href={`#line-${p.item}`}
                                                onClick={(e) => {
                                                    setShowProblems(true);
                                                    scrollToLine(e, p.item!);
                                                }}
                                                className="text-red-700 underline underline-offset-2 dark:text-red-400">
                                                {lineLabel(checklist, p.item)}
                                            </a>{' '}
                                            <span className="text-red-700 dark:text-red-400">
                                                {p.problem === 'unanswered' ? 'is not answered' : p.problem === 'note' ? 'needs a note' : 'needs a photo'}
                                            </span>
                                        </li>
                                    ) : (
                                        <li key={`empty-${i}`} className="text-red-700 dark:text-red-400">
                                            Add at least one line to check.
                                        </li>
                                    ),
                                )}
                            </ul>
                        </div>
                    )}

                    {(['fix_now', 'fix_soon', 'fixed', 'not_needed'] as const).map((grade) => {
                        const listed = items.filter((i) => effectiveGrade(draft.lines[i]) === grade);
                        if (listed.length === 0) return null;
                        return (
                            <div key={grade} className="space-y-2">
                                {listed.map((i) => (
                                    <div key={i} className="flex items-start justify-between gap-3">
                                        <div className="min-w-0">
                                            <div className="flex flex-wrap items-center gap-2">
                                                <GradeBadge grade={grade} />
                                                <span className="font-medium">{lineLabel(checklist, i)}</span>
                                                {grade === 'fixed' && draft.lines[i]?.fixedOnSpot && <span className="text-xs text-muted-foreground">during the inspection</span>}
                                            </div>
                                            {draft.lines[i]?.note && <p className="mt-0.5 text-sm text-muted-foreground">{draft.lines[i].note}</p>}
                                        </div>
                                        <div className="flex shrink-0 gap-1.5">
                                            {draft.lines[i]?.photos.map((u) => (
                                                <PhotoThumb key={u} url={u} previewUrl={previews[u]} alt={lineLabel(checklist, i)} className="h-12 w-12" />
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        );
                    })}

                    {grades.length > 0 && grades.every((g) => g === 'ok') && <p className="text-sm text-muted-foreground">Every line is OK.</p>}

                    {kind === 'monthly' && SLOT_ORDER.some((s) => draft.slots[s]) && (
                        <div className="flex gap-2 overflow-x-auto pb-1">
                            {SLOT_ORDER.filter((s) => draft.slots[s]).map((s) => (
                                <PhotoThumb key={s} url={draft.slots[s]!} previewUrl={previews[draft.slots[s]!]} alt={SLOT_LABEL[s]} caption={SLOT_LABEL[s]} className="h-14 w-14" />
                            ))}
                        </div>
                    )}
                </div>

                {refusal && (
                    <Alert variant="destructive">
                        <AlertTriangle className="h-4 w-4" />
                        <AlertTitle>Not recorded</AlertTitle>
                        <AlertDescription>
                            {refusal.sentence}
                            {refusal.items.length > 0 && <span className="block mt-1">Lines: {refusal.items.map((i) => lineLabel(checklist, i)).join(', ')}.</span>}
                            {REFUSALS_THE_CAR_CHANGED.has(refusal.code) && (
                                <Button variant="outline" className="mt-3 h-11 text-foreground" onClick={() => void reloadCar()}>
                                    <RotateCw aria-hidden />
                                    Reload the car
                                </Button>
                            )}
                        </AlertDescription>
                    </Alert>
                )}
            </Section>

            {/* Submit stays in reach at the foot of the screen. */}
            <div className="sticky bottom-0 z-20 -mx-4 border-t bg-background/95 px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 backdrop-blur sm:mx-0 sm:rounded-t-xl sm:border-x">
                <div className="flex items-center gap-3">
                    <p className="min-w-0 flex-1 text-sm text-muted-foreground">
                        {reloading
                            ? 'Reading the car again…'
                            : uploads.busy
                            ? 'Photos uploading…'
                            : problems.length > 0
                              ? `${problems.length} ${problems.length === 1 ? 'thing' : 'things'} left`
                              : odometerInvalid || lastServiceInvalid
                                ? 'Check the readings'
                                : 'Ready to submit'}
                        {blocked && !uploads.busy && !reloading && (
                            <button type="button" className="ml-2 underline underline-offset-2" onClick={firstProblem}>
                                Show
                            </button>
                        )}
                    </p>
                    <Button className="h-11 px-6" disabled={blocked || mutation.isPending} onClick={() => mutation.mutate()}>
                        {mutation.isPending || reloading ? <Loader2 className="animate-spin" aria-hidden /> : <Send aria-hidden />}
                        Submit
                    </Button>
                </div>
            </div>

            <AlertDialog open={confirmRestart} onOpenChange={setConfirmRestart}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Start again?</AlertDialogTitle>
                        <AlertDialogDescription>Today’s answers and photos for this car are discarded.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Keep the draft</AlertDialogCancel>
                        <AlertDialogAction onClick={restart}>Start again</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
